import {clamp} from 'lodash';
import {useMemo} from 'react';

import {type ClassLabelMap, classLabelMap} from '../../util/media';
import {
  WANDB_BBOX_CLASS_LABEL_KEY,
  WANDB_MASK_CLASS_LABEL_KEY,
} from '../../util/mediaConstants';
import {type RunWithHistoryAndMediaWandb} from '../MediaCard';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode/SingleRunModeContext';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from '../WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {
  type Axis,
  type LayoutMode,
  type MediaBrowserPanelConfig,
  rowsWithKeysMemo,
} from './common';
import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from './Component';
import * as PANEL_MEDIA_BROWSER_CONFIG from './config';
import {defaultColumnCount} from './config';
import {useAllRunsWithColors} from './hooks/useAllRunsWithColors';
import {useMediaSliderKey} from './hooks/useMediaSliderKey';
import {useStepMeta} from './hooks/useStepMeta';
import {
  getCompareConfigDefaults,
  mergeCompareSettingsWithDefaults,
} from './layout/utils/getCompareConfigDefaults';
import {getModeConfig} from './modes/getModeConfig';
import {type StepMeta} from './types';

/**
 * Generates config with smart defaults for all layout modes.
 *
 * For compare mode, this also generates smart column and tile control defaults
 * based on available runtime data (which dimension has the most variation).
 *
 * @param config - Raw user config
 * @param mediaSliderKey - Key to use for step slider
 * @param isSingleRun - Whether viewing a single run
 * @param layoutMode - Current layout mode (gallery/grid/compare)
 * @param runsWithMedia - Optional: Runs with media data (for compare mode defaults)
 * @param stepMeta - Optional: Step metadata (for compare mode defaults)
 * @returns Config with all defaults applied
 */
export function getConfigWithDefaults(
  config: MediaBrowserPanelConfig,
  mediaSliderKey: string | undefined,
  isSingleRun: boolean,
  layoutMode?: LayoutMode | undefined,
  runsWithMedia?: RunWithHistoryAndMediaWandb[],
  stepMeta?: StepMeta
): MediaConfigWithDefaults {
  const gallerySettings =
    config.gallerySettings ??
    (isSingleRun
      ? {
          axis: 'index' as Axis,
        }
      : {
          axis: 'run' as Axis,
        });

  const gridSettings =
    config.gridSettings ??
    (isSingleRun
      ? {
          xAxis: 'step' as Axis,
          yAxis: 'index' as Axis,
        }
      : {xAxis: 'step' as Axis, yAxis: 'run' as Axis});

  const mode = layoutMode ?? config.mode ?? 'gallery';

  const pixelated = config.pixelated ?? true;

  let actualSize = false;
  let fitToDimension = false;
  let videoActualSize = false;
  if (mode === 'gallery') {
    actualSize = config.actualSize ?? false;
    fitToDimension = config.fitToDimension ?? false;
    videoActualSize = config.videoActualSize ?? false;
  }
  const alwaysOriginal = config.alwaysOriginal ?? false;

  const axisValueCount =
    runsWithMedia && stepMeta
      ? getModeConfig(mode).getColumnAxisValueCount(
          gallerySettings,
          gridSettings,
          runsWithMedia,
          stepMeta
        )
      : undefined;
  const columnCount =
    config.columnCount ?? defaultColumnCount(mode, axisValueCount);

  const maxRuns = config.maxRuns ?? DEFAULT_LINE_PLOT_NUM_RUNS_NEW;

  const maxYAxisCount =
    config.maxYAxisCount ?? PANEL_MEDIA_BROWSER_CONFIG.DEFAULT_MAX_Y_AXIS_COUNT;

  const snapToExistingStep = config.snapToExistingStep ?? true;

  const baseConfig: MediaConfigWithDefaults = {
    ...config,
    actualSize,
    fitToDimension,
    videoActualSize,
    columnCount,
    mode,
    pixelated,
    gallerySettings,
    gridSettings,
    maxRuns,
    maxYAxisCount,
    snapToExistingStep,
    mediaSliderKey,
    alwaysOriginal,
  };

  if (mode !== 'compare' || !runsWithMedia || !stepMeta) {
    return baseConfig;
  }

  const clampedColumnCount = clamp(
    baseConfig.columnCount,
    PANEL_MEDIA_BROWSER_CONFIG.MIN_COMPARE_COL_COUNT,
    PANEL_MEDIA_BROWSER_CONFIG.MAX_COMPARE_COL_COUNT
  );

  const runNames = runsWithMedia.map(r => r.name);
  const mediaKeys = baseConfig.mediaKeys ?? [];
  const compareDefaults = getCompareConfigDefaults(
    mediaKeys,
    runNames,
    stepMeta,
    clampedColumnCount
  );
  const compareSettings = mergeCompareSettingsWithDefaults(
    baseConfig.compareSettings,
    compareDefaults
  );

  return {
    ...baseConfig,
    columnCount: clampedColumnCount,
    compareSettings,
  };
}

export const useOverlays = ({
  allRuns,
  mediaKeys,
}: {
  allRuns: RunWithHistoryAndMediaWandb[];
  mediaKeys: string[] | undefined;
}) => {
  const {allMasks, hasMasks, allBoxes, hasBoxes} = useMemo(() => {
    const masks = classLabelMap(
      allRuns,
      mediaKeys ?? [],
      WANDB_MASK_CLASS_LABEL_KEY
    );
    const boxes = classLabelMap(
      allRuns,
      mediaKeys ?? [],
      WANDB_BBOX_CLASS_LABEL_KEY
    );
    return {
      allMasks: masks,
      hasMasks: Object.keys(masks).length > 0,
      allBoxes: boxes,
      hasBoxes: Object.keys(boxes).length > 0,
    };
  }, [allRuns, mediaKeys]);

  return {
    allMasks,
    hasMasks,
    allBoxes,
    hasBoxes,
  };
};

export const useAllRuns = useAllRunsWithColors;

export function useConfigWithDefaults({
  data,
  query,
  config,
  customRunColors,
  pageQuery,
}: Pick<
  MediaBrowserPanelProps,
  'data' | 'query' | 'config' | 'customRunColors' | 'pageQuery'
>): {
  configWithDefaults: MediaConfigWithDefaults;
  allMasks: ClassLabelMap;
  hasMasks: boolean;
  allBoxes: ClassLabelMap;
  hasBoxes: boolean;
} {
  const {mediaKeys, mediaSliderKey: mediaSliderKeyFromConfig} = config;

  const singleRunMode = useSingleRunModeContext();
  const isSingleRun = useMemo(() => {
    return singleRunMode.isSingleRun;
  }, [singleRunMode.isSingleRun]);

  const mediaSliderKey = useMediaSliderKey(mediaSliderKeyFromConfig);

  const allRuns: RunWithHistoryAndMediaWandb[] = useAllRuns({
    data,
    query,
    pageQuery,
    customRunColors,
  });

  const runsWithMedia = useMemo(() => {
    const filtered = allRuns.filter(runHistory => {
      const steps = rowsWithKeysMemo(runHistory, mediaKeys);
      return steps.length > 0;
    });

    return filtered;
  }, [allRuns, mediaKeys]);

  const runStepsMap = useMemo(() => {
    const map = new Map();
    runsWithMedia.forEach(runHistory => {
      map.set(runHistory.name, rowsWithKeysMemo(runHistory, mediaKeys));
    });
    return map;
  }, [runsWithMedia, mediaKeys]);

  const stepMeta = useStepMeta(
    runsWithMedia,
    mediaSliderKey,
    runStepsMap,
    mediaKeys
  );

  const {allMasks, hasMasks, allBoxes, hasBoxes} = useOverlays({
    allRuns,
    mediaKeys,
  });
  const layoutMode: LayoutMode = useMemo(() => {
    return config.mode ?? 'gallery';
  }, [config.mode]);

  const configWithDefaults = useMemo(
    () =>
      getConfigWithDefaults(
        config,
        mediaSliderKey,
        isSingleRun,
        layoutMode,
        runsWithMedia,
        stepMeta
      ),
    [config, isSingleRun, mediaSliderKey, layoutMode, runsWithMedia, stepMeta]
  );

  return {
    configWithDefaults,
    allMasks,
    hasMasks,
    allBoxes,
    hasBoxes,
  };
}
